import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  MOBILE_MAX_WIDTH,
  OUTER_HEIGHT_SETTING,
  TABLE_KEY_COLUMN,
} from '../../../../../../_gori/constants'
import {convertUserTimeZone, useDisabled} from '../../../../../../_gori/helpers'
import {TableSkeleton} from '../../../../../../_gori/helpers/components'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import UseRoutesPermission from '../../../../../../_gori/hooks/UseRoutesPermission'
import {
  Button,
  ConfirmActionSwal,
  DropdownButton,
  Table,
  TableBody,
  TableTabs,
  TableWrapper,
} from '../../../../../../_gori/partials/widgets'
import {useAuth} from '../../../../auth'
import {InviteUserModal, UpdateUserModal, UserManagementFilter} from '../../../../settings'
import UserService from '../../../../users/core/_requests'
import {toast} from 'react-toastify'
import {UseTopAlert} from '../../../../../../_gori/hooks/UseTopAlert'
import clsx from 'clsx'
import useWindowDimensions from '../../../../../../_gori/hooks/useWindowDimensions'

const UserManagementPage: React.FC = () => {
  const intl = useIntl()
  const [responseData, setResponseData] = useState<any>({})
  const [loadingFirst, setLoadingFirst] = useState<Boolean>(true)
  const [statistics, setStatistics] = useState([])
  const [showModalEditUser, setShowModalEditUser] = useState<boolean>(false)
  const [showModalRemoveUser, setShowModalRemoveUser] = useState<boolean>(false)
  const [showModalInviteUser, setShowModalInviteUser] = useState<boolean>(false)
  const [userId, setUserId] = useState<any>('')
  const {loadingSwitch, currentUser} = useAuth()
  const {setPageDisabled} = useDisabled()
  const [searchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()
  const {routes} = UseRoutesPermission()
  const {topAlertHeight} = UseTopAlert()
  const {width} = useWindowDimensions()

  const setTable = useCallback(async () => {
    setPageDisabled(true)
    const config = {
      params: {
        page: searchParams.get('page') || DEFAULT_PAGE,
        page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
        search_text: searchParams.get('search_text') || null,
        status: searchParams.get('status') || null,
      },
      cancelToken: newCancelToken(),
    }
    try {
      const res = await UserService.getListUser(config)
      if (res) {
        setResponseData(res)
        setStatistics(res.statistics)
      }
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setPageDisabled(false)
      setLoadingFirst(false)
    }
  }, [setPageDisabled, searchParams, newCancelToken, isCancel])

  const reload = useCallback(() => {
    setTable()
  }, [setTable])

  const convertNameStatus = useCallback(
    (item) => {
      if (!item.verified) {
        return intl.formatMessage({id: 'PENDING_INVITATION'})
      } else if (item.status) {
        return intl.formatMessage({id: 'ACTIVE'})
      }
      return intl.formatMessage({id: 'INACTIVE'})
    },
    [intl]
  )

  const handleResendInvitation = useCallback(
    async (userId) => {
      setPageDisabled(true)
      await UserService.resend(
        {
          user_id: userId,
        },
        {cancelToken: newCancelToken()}
      )
        .then(() => {
          toast.success(intl.formatMessage({id: 'RESEND_INVITATION_SUCCESSFULLY'}))
          reload()
        })
        .catch((error) => {
          if (isCancel(error)) return
          toast.error(intl.formatMessage({id: 'RESEND_INVITATION_FAILED'}))
        })
        .finally(() => {
          setPageDisabled(false)
          setLoadingFirst(false)
        })
    },
    [setPageDisabled, isCancel, intl, reload, newCancelToken]
  )

  const handleCancelInvitation = useCallback(
    async (userId) => {
      setPageDisabled(true)
      await UserService.cancel(
        {
          user_id: userId,
        },
        {cancelToken: newCancelToken()}
      )
        .then(() => {
          toast.success(intl.formatMessage({id: 'CANCEL_INVITATION_SUCCESSFULLY'}))
          reload()
        })
        .catch((error) => {
          if (isCancel(error)) return
          toast.error(intl.formatMessage({id: 'CANCEL_INVITATION_FAILED'}))
        })
        .finally(() => {
          setPageDisabled(false)
          setLoadingFirst(false)
        })
    },
    [setPageDisabled, isCancel, intl, reload, newCancelToken]
  )

  const columns = useMemo(() => {
    return [
      {
        Header: intl.formatMessage({id: 'FULL_NAME'}),
        headerClassName: 'min-w-150px',
        cellClassName: 'text-dark',
        Cell: ({row}: {row: any}) => {
          return (row.original.first_name || '') + ' ' + (row.original.last_name || '')
        },
      },
      {
        Header: intl.formatMessage({id: 'EMAIL'}),
        headerClassName: 'min-w-80px',
        Cell: ({row}: {row: any}) => {
          return row.original.email
        },
      },
      {
        Header: intl.formatMessage({id: 'STATUS'}),
        headerClassName: 'min-w-125px text-center',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => {
          return (
            <span
              className={`d-flex justify-content-center px-4 py-1 rounded-pill status-user-${convertNameStatus(
                row.original
              )} rounded-pill`}
            >
              {convertNameStatus(row.original)}
            </span>
          )
        },
      },
      {
        Header: intl.formatMessage({id: 'MEMBER_SINCE'}),
        headerClassName: 'min-w-125px',
        Cell: ({row}: {row: any}) => {
          return convertUserTimeZone(
            row.original?.created_at,
            currentUser,
            process.env.REACT_APP_DATE_FORMAT
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.ACTIONS,
        Header: intl.formatMessage({id: 'ACTIONS'}),
        headerClassName: 'fixed-action',
        cellClassName: 'fixed-action',
        Cell: ({row}: {row: any}) =>
          routes.USERS_UPDATE.hasPermission && (
            <Button
              disabled={!row.original.verified}
              className='btn btn-advanced-primary btn-sm'
              event={() => {
                setUserId(row.original.id)
                setShowModalEditUser(true)
              }}
            >
              {intl.formatMessage({id: 'EDIT'})}
            </Button>
          ),
      },
      {
        id: TABLE_KEY_COLUMN.SUB_ACTIONS,
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          let listDropDownBtn = [
            {
              label: intl.formatMessage({id: 'REMOVE'}),
              action: () => {
                setUserId(row.original.id)
                setShowModalRemoveUser(true)
              },
              className: 'cursor-pointer',
              hidden: !routes.USERS_REMOVE.hasPermission,
            },
          ]

          if (!row.original.verified) {
            listDropDownBtn = [
              {
                label: intl.formatMessage({id: 'RESEND_INVITATION'}),
                action: () => {
                  handleResendInvitation(row.original.id)
                },
                className: 'cursor-pointer',
                hidden: !routes.USERS_INVITE.hasPermission,
              },
              {
                label: intl.formatMessage({id: 'CANCEL_INVITATION'}),
                action: () => {
                  handleCancelInvitation(row.original.id)
                },
                className: 'cursor-pointer',
                hidden: !routes.USERS_INVITE.hasPermission,
              },
            ]
          }

          return <DropdownButton loading={row.original.isLoading} list={listDropDownBtn} />
        },
      },
    ]
  }, [currentUser, intl, routes, convertNameStatus, handleCancelInvitation, handleResendInvitation])

  const handleRemoveUser = useCallback(async () => {
    setPageDisabled(true)
    await UserService.removeFromCompany(userId)
      .then(() => {
        toast.success(intl.formatMessage({id: 'REMOVE_SUCCESSFULLY'}))
        reload()
      })
      .catch((error) => {
        if (isCancel(error)) return
        if (error.response.data.error.message) {
          toast.error(error.response.data.error.message)
        } else {
          toast.error(intl.formatMessage({id: 'REMOVE_FAILED'}))
        }
      })
      .finally(() => {
        setPageDisabled(false)
        setLoadingFirst(false)
      })
  }, [setPageDisabled, isCancel, userId, intl, reload])

  useEffect(() => {
    if (loadingSwitch) return
    setTable()

    return () => {}
  }, [setTable, loadingSwitch])

  return (
    <>
      {showModalEditUser && (
        <UpdateUserModal
          show={showModalEditUser}
          handleClose={() => {
            setShowModalEditUser(false)
          }}
          handleUpdateUserSuccess={reload}
          userId={userId}
        />
      )}
      {showModalInviteUser && (
        <InviteUserModal
          show={showModalInviteUser}
          handleClose={() => {
            setShowModalInviteUser(false)
          }}
          reloadTable={reload}
        />
      )}

      <ConfirmActionSwal
        show={showModalRemoveUser}
        title={intl.formatMessage({id: 'DELETE_USER'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleRemoveUser}
        handleClose={() => setShowModalRemoveUser(false)}
      />
      <div className='d-flex justify-content-start w-100 settings-header table-settings-header'>
        <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1'>
          {intl.formatMessage({id: 'USER_MANAGEMENT'})}
        </h1>
      </div>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          <>
            <TableWrapper>
              <div className='table-content--inset-inner h-100'>
                <TableBody>
                  <div className='mt-6 d-flex  align-item-center justify-content-between'>
                    <div className='col-3'>
                      <span className='fs-1 col-12 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
                    </div>
                    <div className='col-2 d-flex flex-row-reverse'>
                      <span className='btn col-5 placeholder placeholder-lg rounded-2 bg-secondary' />
                    </div>
                  </div>
                  <hr className='mb-8' />
                  <TableSkeleton countRow={8} />
                </TableBody>
              </div>
            </TableWrapper>
          </>
        ) : (
          <>
            <TableTabs
              dataTabs={statistics}
              keyCheckActive='status'
              children={
                <TableWrapper className=''>
                  <div className='table-content--inset-inner h-100'>
                    <UserManagementFilter />
                    <TableBody height={`calc(100vh - ${OUTER_HEIGHT_SETTING + topAlertHeight}px)`}>
                      <Table
                        columns={columns}
                        data={responseData?.users || []}
                        pagination={responseData?.pagination || {}}
                        tbodyClass='text-gray-600 fw-bold'
                        usePagination
                        useCheckBox
                      />
                    </TableBody>
                  </div>
                </TableWrapper>
              }
              childrenTab={
                <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
                  <>
                    {routes.USERS_INVITE.hasPermission && (
                      <Button
                        className={clsx('btn btn-advanced-primary fs-5 mb-3', {
                          'w-100': width <= MOBILE_MAX_WIDTH,
                        })}
                        label={intl.formatMessage({id: 'INVITE_USER'})}
                        event={() => setShowModalInviteUser(true)}
                      />
                    )}
                  </>
                </CSSTransition>
              }
            />
          </>
        )}
      </CSSTransition>
    </>
  )
}

export {UserManagementPage}
